<template>
  <span class="round-time-select">
    <template v-if="isYearUnit">
      <input-number
        :value="year"
        :step="minYear ? yearStep : 1"
        :min="minYear"
        :max="maxYear"
        @update:value="updateYear"
      />

      <template v-if="roundUnit === 'halfYear'">
        <input-dropdown
          dropdown-class="dropdown-slim"
          class="round-time-select__dropdown"
          :options="availableHalfYears"
          :value="{ id: halfYear }"
          prop="label"
          @update:value="updateHalfYear(year, $event.id)"
        />
      </template>

      <template v-if="roundUnit === 'quarter'">
        <input-dropdown
          dropdown-class="dropdown-slim"
          class="round-time-select__dropdown"
          :options="availableQuarters"
          :value="{ id: quarter }"
          prop="label"
          @update:value="updateQuarter(year, $event.id)"
        />
      </template>

      <template v-if="roundUnit === 'month'">
        <input-number
          :value="month"
          :min="minMonth"
          :max="maxMonth"
          @update:value="updateMonth(year, $event)"
        />
      </template>
    </template>

    <template v-else-if="roundUnit === 'week' || roundUnit === 'day'">
      <input-text
        type="date"
        :value="dateString"
        :step="dateStep"
        :min="minDateString"
        :max="maxDateString"
        @update:value="updateDate"
      />
    </template>

    <template v-else>
      <input-text
        type="date"
        :value="dateString"
        :min="minDateString"
        :max="maxDateString"
        @update:value="updateDateTime($event, timeString)"
      />
      <input-text
        v-if="isTimeUnit"
        type="time"
        :value="timeString"
        :step="timeStep"
        :min="minTime"
        :max="maxTime"
        @update:value="updateDateTime(dateString, $event)"
      />
    </template>
  </span>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import {
  isTimeUnit,
  isYearUnit
} from "@editor/items/simulationRound/useRoundTime";
import InputText from "@core/input/InputText.vue";
import InputNumber from "@core/input/InputNumber.vue";
import InputDropdown from "@core/input/InputDropdown.vue";
import { DateTime } from "luxon";

import type { PropType } from "vue";
import type { RoundUnit } from "./SimulationRoundItem";

export default defineComponent({
  components: { InputText, InputNumber, InputDropdown },
  props: {
    roundUnit: {
      type: String as PropType<RoundUnit>,
      required: true
    },
    roundLength: {
      type: Number,
      default: 1
    },
    value: {
      type: String,
      required: true
    },
    min: {
      type: String,
      default: null
    },
    max: {
      type: String,
      default: null
    },
    timeType: {
      type: String,
      default: "start",
      validator(value: "start" | "end") {
        return ["start", "end"].includes(value);
      }
    }
  },
  emits: ["update:value"],
  computed: {
    date() {
      return new Date(this.value);
    },
    year() {
      return this.date.getUTCFullYear();
    },
    month() {
      return this.date.getUTCMonth() + 1;
    },
    halfYear() {
      return this.month <= 6 ? 1 : 2;
    },
    quarter() {
      if (this.month <= 3) return 1;
      if (this.month <= 6) return 2;
      if (this.month <= 9) return 3;
      if (this.month <= 12) return 4;
      return 1;
    },
    dateString() {
      return this.date.toISOString().substring(0, 10);
    },
    timeString() {
      const hour = this.date.getUTCHours().toString().padStart(2, "0");
      if (this.roundUnit === "hour") {
        if (this.timeType == "start") return `${hour}:00`;
        return `${hour}:59`;
      }

      const minute = this.date.getUTCMinutes().toString().padStart(2, "0");
      if (this.roundUnit === "minute") {
        return `${hour}:${minute}`;
      }

      const second = this.date.getUTCSeconds().toString().padStart(2, "0");
      return `${hour}:${minute}:${second}`;
    },

    isYearUnit() {
      return isYearUnit(this.roundUnit);
    },
    isTimeUnit() {
      return isTimeUnit(this.roundUnit);
    },

    yearStep() {
      if (!this.minDate) return 1;
      if (this.roundUnit === "century") return this.roundLength * 100;
      if (this.roundUnit === "decade") return this.roundLength * 10;
      if (this.roundUnit === "year") return this.roundLength;
      return 1;
    },
    dateStep() {
      if (!this.minDate) return 1;
      if (this.roundUnit === "week") return this.roundLength * 7;
      if (this.roundUnit === "day") return this.roundLength;
      return 1;
    },
    timeStep() {
      if (this.roundUnit === "second") return 1;
      if (this.roundUnit === "minute") return 60;
      if (this.roundUnit === "hour") return 3600;
      return 60;
    },

    minDate() {
      if (!this.min) return;
      return new Date(this.min);
    },
    minDateString() {
      if (this.minDate == null) return;
      return this.minDate.toISOString().substring(0, 10);
    },
    minYear() {
      if (this.minDate == null) return;
      return this.minDate.getUTCFullYear();
    },
    minMonth() {
      if (this.minDate == null) return 1;
      if (this.minYear != null && this.year > this.minYear) return 1;
      return this.minDate.getUTCMonth() + 1;
    },
    minHalfYear() {
      if (!this.min) return 1;
      if (this.minYear != null && this.year > this.minYear) return 1;
      if (this.minMonth >= 7) return 2;
      return 1;
    },
    minQuarter() {
      if (!this.min) return 1;
      if (this.minYear != null && this.year > this.minYear) return 1;
      if (this.minMonth >= 10) return 4;
      if (this.minMonth >= 7) return 3;
      if (this.minMonth >= 4) return 2;
      return 1;
    },
    minTime() {
      if (!this.minDate) return "00:00";
      if (this.dateString != this.minDateString) return "00:00";

      const hour = this.minDate.getUTCHours().toString().padStart(2, "0");
      if (this.roundUnit === "hour") {
        if (this.timeType === "start") return `${hour}:00`;
        return `${hour}:59`;
      }

      const minute = this.minDate.getUTCMinutes().toString().padStart(2, "0");
      if (this.roundUnit === "minute") return `${hour}:${minute}`;

      const second = this.minDate.getUTCSeconds().toString().padStart(2, "0");
      return `${hour}:${minute}:${second}`;
    },

    maxDate() {
      if (!this.max) return null;
      return new Date(this.max);
    },
    maxDateString() {
      if (this.maxDate == null) return;
      return this.maxDate.toISOString().substring(0, 10);
    },
    maxYear() {
      if (this.maxDate == null) return;
      return this.maxDate.getUTCFullYear();
    },
    maxMonth() {
      if (!this.maxDate) return 12;
      if (this.maxYear != null && this.year < this.maxYear) return 12;
      return this.maxDate.getUTCMonth() + 1;
    },
    maxHalfYear() {
      if (this.max == null) return 2;
      if (this.maxYear != null && this.year < this.maxYear) return 2;
      if (this.maxMonth < 7) return 1;
      return 2;
    },
    maxQuarter() {
      if (this.max == null) return 4;
      if (this.maxYear != null && this.year < this.maxYear) return 4;
      if (this.maxMonth >= 10) return 4;
      if (this.maxMonth >= 7) return 3;
      if (this.maxMonth >= 4) return 2;
      return 1;
    },
    maxTime() {
      if (!this.maxDate) return "23:59";
      if (this.dateString != this.maxDateString) return "00:00";

      const hour = this.maxDate.getUTCHours().toString().padStart(2, "0");
      if (this.roundUnit === "hour") {
        if (this.timeType === "start") return `${hour}:00`;
        return `${hour}:59`;
      }

      const minute = this.maxDate.getUTCMinutes().toString().padStart(2, "0");
      if (this.roundUnit === "minute") return `${hour}:${minute}`;

      const second = this.maxDate.getUTCSeconds().toString().padStart(2, "0");
      return `${hour}:${minute}:${second}`;
    },

    availableQuarters() {
      const quarters = [];
      for (let i = 1; i <= 4; i++) {
        const quarter = {
          id: i,
          disabled: i < this.minQuarter || i > this.maxQuarter,
          label: `${i}`
        };
        quarters.push(quarter);
      }
      return quarters;
    },

    availableHalfYears() {
      const halfYears = [];
      for (let i = 1; i <= 2; i++) {
        const halfYear = {
          id: i,
          disabled: i < this.minHalfYear || i > this.maxHalfYear,
          label: `${i}`
        };
        halfYears.push(halfYear);
      }
      return halfYears;
    }
  },
  methods: {
    updateYear(year: number) {
      if (this.roundUnit === "month") {
        return this.updateMonth(year, this.month);
      } else if (this.roundUnit === "halfYear") {
        return this.updateHalfYear(year, this.halfYear);
      } else if (this.roundUnit === "quarter") {
        return this.updateQuarter(year, this.quarter);
      } else {
        if (this.timeType == "start") {
          this.updateDate(DateTime.utc(year));
        } else {
          this.updateDate(DateTime.utc(year, 12, 31, 23, 59, 59));
        }
      }
    },
    updateHalfYear(year: number, halfYear: number) {
      const month = halfYear === 1 ? 1 : 7;
      return this.updateMonth(year, month);
    },
    updateQuarter(year: number, quarter: number) {
      let month = 1;
      if (quarter == 1) month = 1;
      if (quarter == 2) month = 4;
      if (quarter == 3) month = 7;
      if (quarter == 4) month = 10;
      return this.updateMonth(year, month);
    },
    updateMonth(year: number, month: number) {
      if (this.timeType === "start") {
        return this.updateDate(DateTime.utc(year, month).startOf("month"));
      } else {
        return this.updateDate(DateTime.utc(year, month).endOf("month"));
      }
    },

    updateDate(date: string | DateTime) {
      if (!date) {
        console.warn(`${date} is not a date`);
        return;
      }
      if (typeof date === "string") {
        date = DateTime.fromISO(date + "T00:00:00.000Z"); // make sure we parse UTC time
      }
      date = date.toUTC();
      if (this.timeType === "end") {
        date = date.endOf("day");
      }

      this.updateValue(date);
    },

    updateDateTime(dateString: string, time: string) {
      if (!dateString) {
        console.warn(`${dateString} is not a date`);
        return;
      }

      // format time to correct unit
      if (this.roundUnit === "hour") time = time.substring(0, 2) + ":00:00";
      else if (this.roundUnit === "minute") time = time.substring(0, 5) + ":00";
      else if (time.length < 6) time += ":00";

      let d = DateTime.fromISO(dateString + "T" + time + ".000Z").toUTC(); // make sure we parse UTC time

      if (this.timeType == "end") {
        let timeUnit = this.roundUnit;
        if (timeUnit == "round") timeUnit = "day";
        if (timeUnit == "century") timeUnit = "year";
        if (timeUnit == "decade") timeUnit = "year";
        if (timeUnit == "halfYear") timeUnit = "month";
        d = d.endOf(timeUnit);
      }

      this.updateValue(d);
    },

    updateValue(dateTime: DateTime) {
      this.$emit("update:value", dateTime.toISO());
    }
  }
});
</script>

<style lang="scss">
.round-time-select {
  display: inline-flex;
  align-items: start;
  gap: 0.5rem;

  &__dropdown {
    min-width: 6rem;
  }
}
</style>
